import { UseMutationResult } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import React, { FC, useEffect, useState } from 'react'
import { Box, Button, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'

import { PersonalSettingsDto } from '@/types.ts'
import { useDialog } from '@/hooks'
import { SubmitErrorMessage } from '@/components/form'
import { Dialog } from '@/components/dialog.tsx'
import { Icon } from '@/components/icon.tsx'
import { ToggleGroup } from '@/components/toggle-group.tsx'
import { DndComponent } from '@/components/dnd-area/dnd-component.tsx'
import { RowProps } from '@/components/dnd-area/draggable-row.tsx'

export interface PersonCustomizationDialogProps {
  personalSettingsKey: string
  itemsDesktop: RowProps[]
  itemsMobile: RowProps[]
  mutation: UseMutationResult<AxiosResponse<void | 'OK'>, Error, PersonalSettingsDto, unknown>
  onReorder?: (newOrder: Record<string, string[]>) => void
}

export const PersonCustomizationDialog: FC<PersonCustomizationDialogProps> = ({
  mutation,
  itemsMobile,
  itemsDesktop,
  onReorder,
  personalSettingsKey,
}): React.ReactElement => {
  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()
  const [toggleValue, setToggleValue] = useState<'mobile' | 'desktop'>('desktop')
  const [orderDesktop, setOrderDesktop] = useState<RowProps[]>(itemsDesktop)
  const [orderMobile, setOrderMobile] = useState<RowProps[]>(itemsMobile)
  const { mutateAsync, isError, reset } = mutation
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    setOrderDesktop(itemsDesktop)
  }, [itemsDesktop])

  useEffect(() => {
    setOrderMobile(itemsMobile)
  }, [itemsMobile])

  const handleSaveSubmit = async () => {
    try {
      const newOrder: Record<string, string[]> = {}
      newOrder['desktop'] = orderDesktop.filter((e) => e.isChecked).map((e) => e.id)
      newOrder['mobile'] = orderMobile.filter((e) => e.isChecked).map((e) => e.id)

      if (!personalSettingsKey) return

      const response = await mutateAsync({
        pages: { [personalSettingsKey]: newOrder },
      })

      if (response?.status === 200) {
        onReorder?.(newOrder)
        handleHideDialog()
        reset()
      }

      if (response?.status !== 200) {
        setErrorMessage('Произошла ошибка при сохранение настроек, попробуйте позже')
      }
    } catch (axiosError) {
      setErrorMessage('Произошла непредвиденная ошибка')
    }
  }

  return (
    <Box display="flex" justifyContent="flex-end">
      <Tooltip title="Персональные настройки">
        <IconButton color="secondary" onClick={handleShowDialog}>
          <Icon name="Settings" size="px32" />
        </IconButton>
      </Tooltip>

      {showDialog && (
        <Dialog
          title="Персональные настройки страницы"
          open={showDialog}
          onClose={handleHideDialog}
          content={
            <Stack flexDirection="column" spacing={2}>
              <Typography variant="body2" color="text.main">
                Столбцы таблицы
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Выберите порядок столбцов, а также отметьте какие из них будут отображаться
              </Typography>

              <DndComponent
                keyArea={toggleValue}
                items={toggleValue === 'mobile' ? orderMobile : orderDesktop}
                onReorder={toggleValue === 'mobile' ? setOrderMobile : setOrderDesktop}
              />

              <ToggleGroup
                width="fill"
                items={[
                  { title: 'Для ПК Формата', value: 'desktop' },
                  { title: 'Для мобильного формата', value: 'mobile' },
                ]}
                value={toggleValue}
                onChange={(_, item) => setToggleValue(item)}
              />
              <Grid direction="column" spacing={1} container>
                <Grid>
                  <SubmitErrorMessage
                    message={isError ? errorMessage : 'Произошла непредвиденная ошибка, попробуйте ещё раз'}
                    isError={isError}
                  />
                </Grid>
                <Grid>
                  <Button fullWidth onClick={handleSaveSubmit}>
                    Сохранить
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          }
        />
      )}
    </Box>
  )
}
